import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost/api/v1',
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor - add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - handle errors and capture headers
api.interceptors.response.use(
    (response) => {
        // Capture warning headers from backend (non-blocking)
        try {
            const currentPlan = response.headers['x-plan'];
            const usageWarning = response.headers['x-usage-warning'];
            const usagePercentage = response.headers['x-usage-percentage'];

            // Update warning store if any warnings exist
            if (usageWarning || currentPlan) {
                // Lazy import to avoid circular dependencies - don't await
                import('../store/warningStore').then(({ useWarningStore }) => {
                    useWarningStore.getState().setWarning({
                        currentPlan: currentPlan as any,
                        usageWarning: usageWarning as any,
                        usagePercentage: usagePercentage ? parseFloat(usagePercentage) : null,
                    });
                }).catch(() => {
                    // Silently fail - warning store is optional
                });
            }
        } catch (err) {
            // Don't block response if header processing fails
        }

        return response;
    },
    (error: AxiosError<any>) => {
        if (error.response) {
            const { status, data } = error.response;

            // Handle unauthorized (except login/register endpoints)
            if (status === 401 && !error.config?.url?.includes('/auth/login') && !error.config?.url?.includes('/auth/register')) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                toast.error('Session expired. Please login again.');
                return Promise.reject(error);
            }

            const isAuthEndpoint = error.config?.url?.includes('/auth/');
            const isAiEndpoint = error.config?.url?.includes('/settings/ai');
            const isIntelligenceEndpoint = error.config?.url?.includes('/intelligence/');
            const isSdkRateLimitEndpoint = error.config?.url?.includes('/sdk-rate-limit');
            const isBillingEndpoint = error.config?.url?.includes('/billing/');
            const isDataDeletionEndpoint = error.config?.url?.includes('/data-deletion/');

            // For auth endpoints, let mutation handle all errors
            if (isAuthEndpoint) {
                return Promise.reject(error);
            }

            // For AI settings endpoints, let mutation handle all errors
            if (isAiEndpoint) {
                return Promise.reject(error);
            }

            // For billing endpoints, let mutation handle all errors
            // This includes upgrade errors like DOWNGRADE_NOT_ALLOWED, TEAM_SIZE_EXCEEDED
            if (isBillingEndpoint) {
                return Promise.reject(error);
            }

            // For data deletion endpoints, let mutation handle all errors
            // This includes validation errors like incorrect password, wrong confirmation text
            if (isDataDeletionEndpoint) {
                return Promise.reject(error);
            }

            // For intelligence endpoints, let component handle all errors (no toast)
            // 404 is normal when no intelligence data exists yet
            if (isIntelligenceEndpoint) {
                return Promise.reject(error);
            }

            // For SDK rate limit endpoint, let component handle all errors (no toast)
            // 404 is normal when no API keys exist or feature not available
            if (isSdkRateLimitEndpoint) {
                return Promise.reject(error);
            }

            // Handle specific SDK limit errors with special UI
            const errorCode = data?.error?.code;

            if (errorCode === 'TRIAL_EXPIRED' || errorCode === 'PAYMENT_LIMIT_EXCEEDED') {
                // These errors should show special modals, not toasts
                // They will be handled by a global error boundary or specific components
                return Promise.reject(error);
            }

            // Handle specific error codes FIRST (let mutation handle them)
            // Backend can send: { error: { code: "...", message: "..." } }
            const skipToastCodes = [
                'FEATURE_NOT_AVAILABLE',
                'USER_ALREADY_MEMBER',
                'INVITATION_NOT_FOUND',
                'MEMBER_NOT_FOUND',
                'CANNOT_REMOVE_OWNER',
                'INVALID_CREDENTIALS',
                'EMAIL_ALREADY_EXISTS',
                'INVALID_AI_CONFIG', // AI provider test errors
                'TEAM_LIMIT_REACHED', // Team member limit errors
                'PROVIDER_LIMIT_EXCEEDED', // Provider limit errors
            ];

            if (errorCode && skipToastCodes.includes(errorCode)) {
                // Don't show toast here - mutation onError will handle it
                return Promise.reject(error);
            }

            // Handle validation errors (çoğul format)
            // Backend can send: { errors: { field: ["message1", "message2"] } }
            if (data.errors && typeof data.errors === 'object') {
                const errorMessages = Object.values(data.errors).flat();
                errorMessages.forEach((message: any) => toast.error(message));
                return Promise.reject(error);
            }

            // Handle single error object (tekil format)
            // Backend can send: { error: "Error message" } or { error: { message: "..." } }
            if (data.error) {
                if (typeof data.error === 'string') {
                    toast.error(data.error);
                } else if (data.error.message) {
                    toast.error(data.error.message);
                } else {
                    toast.error('An error occurred. Please try again.');
                }
                return Promise.reject(error);
            }

            // Handle message field
            // Backend can send: { message: "Error message" }
            if (data.message) {
                toast.error(data.message);
                return Promise.reject(error);
            }

            // Fallback
            toast.error('An error occurred. Please try again.');
        } else if (error.request) {
            toast.error('Network error. Please check your connection.');
        } else {
            toast.error('An unexpected error occurred.');
        }

        return Promise.reject(error);
    }
);

export default api;
